import React, { useMemo, useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Chip,
  TextField,
  Divider,
  Alert,
  useMediaQuery,
  Collapse,
  IconButton,
} from "@mui/material";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { styled, keyframes, useTheme } from "@mui/material/styles";
import api from "../Api";

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 113, 91, 0.55); }
  70% { box-shadow: 0 0 0 10px rgba(255, 113, 91, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 113, 91, 0); }
`;

const GradientDialog = styled(Dialog)(() => ({
  "& .MuiDialog-paper": {
    borderRadius: 18,
    background: "linear-gradient(135deg, #101820 0%, #233443 100%)",
    color: "#ffffff",
    overflow: "visible",
    border: "1px solid rgba(255, 113, 91, 0.55)",
    boxShadow: "0 18px 60px rgba(0,0,0,0.55)",
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: -38,
  left: "50%",
  transform: "translateX(-50%)",
  width: 78,
  height: 78,
  borderRadius: "50%",
  background: "linear-gradient(135deg, #ff715b, #e65a45)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  boxShadow: "0 14px 30px rgba(255, 113, 91, 0.35)",
  border: "4px solid #101820",
  [theme.breakpoints.down("sm")]: {
    top: -34,
    width: 72,
    height: 72,
  },
}));

const FeatureRow = ({ icon, title, subtitle }) => (
  <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
    <Box sx={{ mt: 0.25 }}>{icon}</Box>
    <Box>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 800, color: "white", lineHeight: 1.1 }}
      >
        {title}
      </Typography>
      <Typography variant="caption" sx={{ color: "rgba(176,190,197,0.92)" }}>
        {subtitle}
      </Typography>
    </Box>
  </Box>
);

const BetaPioneerDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Redeem state
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redeemFeedback, setRedeemFeedback] = useState({ type: "", text: "" }); // "success" | "error" | ""

  const normalizedCode = useMemo(() => redeemCode.trim(), [redeemCode]);

  // ✅ scroll helper
  const redeemBottomRef = useRef(null);

  const scrollRedeemToBottom = () => {
    // scrolls inside the DialogContent (paper) automatically
    redeemBottomRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  };

  // ✅ when the Alert appears, scroll down to it
  useEffect(() => {
    if (redeemFeedback?.text) {
      requestAnimationFrame(scrollRedeemToBottom);
    }
  }, [redeemFeedback?.text]);

  const handleRedeem = async () => {
    setRedeemFeedback({ type: "", text: "" });

    if (!normalizedCode) {
      setRedeemFeedback({ type: "error", text: "Please enter a code." });
      return;
    }

    setRedeemLoading(true);
    try {
      // Api.js baseURL already points to /api, so we call /SecretCode/Redeem
      const res = await api.post("/SecretCode/Redeem", { code: normalizedCode });
      setRedeemFeedback({
        type: "success",
        text: res?.data?.message || "Code redeemed successfully!",
      });
      const newBalance = Number(res?.data?.newBalance ?? res?.data?.newAmount);
      if (Number.isFinite(newBalance)) {
        window.dispatchEvent(new CustomEvent("budgetUpdated", { detail: newBalance }));
      }
      setRedeemCode("");
    } catch (err) {
      const serverMsg = err?.response?.data;
      setRedeemFeedback({
        type: "error",
        text:
          typeof serverMsg === "string"
            ? serverMsg
            : "Redemption failed. Please try again.",
      });

  
  
  
  
    } finally {
      setRedeemLoading(false);
    }
  };

  const handleContinue = () => {
    localStorage.setItem("showAiAdDialog", "false");
    onClose();
  };

  return (
    <GradientDialog
      open={open}
      onClose={handleContinue}
      maxWidth="xs"
      fullWidth
      aria-labelledby="beta-dialog-title"
    >
      {/* Floating Rocket Icon */}
      <IconWrapper>
        <RocketLaunchIcon sx={{ fontSize: isMobile ? 36 : 40, color: "white" }} />
      </IconWrapper>

      <DialogContent
        sx={{
          pt: 6.5,
          pb: 2,
          px: { xs: 2, sm: 3 },
          textAlign: "center",

          // ✅ hide the ugly scrollbar but keep scroll
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {/* Status Badge */}
        <Chip
          label="BETA PIONEER ACTIVE"
          size="small"
          sx={{
            bgcolor: "rgba(255, 113, 91, 0.16)",
            color: "#ff715b",
            border: "1px solid rgba(255, 113, 91, 0.55)",
            fontWeight: 900,
            mb: 1.5,
            fontSize: "0.72rem",
            letterSpacing: "0.6px",
          }}
        />

        <Typography
          sx={{
            fontWeight: 900,
            color: "#ffffff",
            letterSpacing: "0.4px",
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            lineHeight: 1.15,
            mb: 1,
          }}
        >
          Welcome to the Inner Circle
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "rgba(176,190,197,0.92)",
            mb: 2,
            lineHeight: 1.65,
            fontSize: { xs: "0.86rem", sm: "0.9rem" },
          }}
        >
          Thank you for being an early adopter of Contactie. As a founding
          member, your account holds a special status within our ecosystem.
        </Typography>

        {/* Value Props / Benefits */}
        <Paper
          elevation={0}
          sx={{
            bgcolor: "rgba(255,255,255,0.04)",
            borderRadius: 2,
            p: 2,
            textAlign: "left",
            mb: 2,
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <FeatureRow
              icon={<VerifiedUserIcon sx={{ color: "#ff715b", fontSize: 20 }} />}
              title="Distinguished Profile"
              subtitle='Your profile is marked with the exclusive "Pioneer Rocket" badge.'
            />
            <FeatureRow
              icon={<AutoGraphIcon sx={{ color: "#ff715b", fontSize: 20 }} />}
              title="Algorithm Priority"
              subtitle="Your feedback directly shapes the evolution of the Trust Score."
            />
          </Box>
        </Paper>

        {/* Collapsible Redeem header */}
        <Divider sx={{ my: 1.8, opacity: 0.18 }} />

        <Paper
          elevation={0}
          sx={{
            bgcolor: "rgba(0,0,0,0.14)",
            borderRadius: 2,
            border: "1px solid rgba(255,255,255,0.08)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1.6,
              py: 1.2,
            }}
          >
            <Box sx={{ textAlign: "left" }}>
              <Typography sx={{ fontWeight: 900, color: "white", fontSize: "0.95rem" }}>
                Have a code?
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(176,190,197,0.92)" }}>
                Redeem credits for digital bundles & coupons (no hardware).
              </Typography>
            </Box>

            <IconButton
              onClick={() => setRedeemOpen((v) => !v)}
              sx={{
                color: "#ff715b",
                bgcolor: "rgba(255, 113, 91, 0.10)",
                "&:hover": { bgcolor: "rgba(255, 113, 91, 0.18)" },
              }}
              aria-label="toggle redeem section"
            >
              {redeemOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse
            in={redeemOpen}
            timeout={220}
            unmountOnExit
            onEntered={scrollRedeemToBottom}
          >
            <Box sx={{ px: 1.6, pb: 1.6 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: "stretch",
                  mt: 0.6,
                }}
              >
                <TextField
                  value={redeemCode}
                  onChange={(e) => setRedeemCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleRedeem();
                  }}
                  placeholder="Enter code (e.g. ABCD1234EFGH)"
                  size="small"
                  fullWidth
                  autoComplete="off"
                  inputProps={{ style: { color: "white" } }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "rgba(255,255,255,0.03)",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.18)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.32)",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                      { borderColor: "#ff715b" },
                  }}
                />

                <Button
                  onClick={handleRedeem}
                  variant="contained"
                  disabled={redeemLoading}
                  fullWidth={isMobile}
                  sx={{
                    bgcolor: "#ff715b",
                    "&:hover": { bgcolor: "#e65a45" },
                    fontWeight: 900,
                    borderRadius: 2,
                    minWidth: { sm: 120 },
                    py: 1,
                  }}
                >
                  {redeemLoading ? "Redeeming..." : "Redeem"}
                </Button>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 1.1,
                  color: "rgba(176,190,197,0.92)",
                  textAlign: "left",
                  lineHeight: 1.4,
                }}
              >
                Note: Redeemed credits can be used only for <b>digital internal bundles</b>{" "}
                and <b>coupons</b>. They cannot be used for <b>hardware purchases</b>.
              </Typography>

              {redeemFeedback?.text && (
                <Alert
                  severity={redeemFeedback.type === "success" ? "success" : "error"}
                  sx={{
                    mt: 1.2,
                    bgcolor: "rgba(255,255,255,0.05)",
                    color: "white",
                    border: "1px solid rgba(255,255,255,0.10)",
                    "& .MuiAlert-icon": { color: "#ff715b" },
                  }}
                >
                  {redeemFeedback.text}
                </Alert>
              )}

              {/* ✅ anchor used by scrollIntoView */}
              <Box ref={redeemBottomRef} />
            </Box>
          </Collapse>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1.5, justifyContent: "center" }}>
        <Button
          onClick={handleContinue}
          variant="contained"
          fullWidth
          sx={{
            bgcolor: "#ff715b",
            color: "white",
            py: 1.45,
            borderRadius: 2,
            fontWeight: 900,
            fontSize: "1rem",
            animation: `${pulse} 2s infinite`,
            "&:hover": { bgcolor: "#e65a45" },
          }}
        >
          ENTER DASHBOARD
        </Button>
      </DialogActions>
    </GradientDialog>
  );
};

export default BetaPioneerDialog;
